import { jwtDecode } from 'jwt-decode'

const requestCache = new Map()
const CACHE_TIME = 1000 // 1 second cache time

const handleAuthFailure = () => {
  window.dispatchEvent(new CustomEvent('auth:failure'))
}

const refreshAccessToken = async () => {
  const refreshUrl = `${import.meta.env.VITE_API_BASE_URL}/auth/token`
  const refreshToken = localStorage.getItem('refreshToken')

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      throw new Error('Failed to refresh access token')
    }

    const { accessToken } = await response.json()
    localStorage.setItem('accessToken', accessToken)
    return accessToken
  } catch (error) {
    console.error('Error refreshing access token:', error)
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    throw error
  }
}

const isTokenExpired = (token) => {
  if (!token) return true
  try {
    const decodedToken = jwtDecode(token)
    return decodedToken.exp * 1000 < Date.now()
  } catch (error) {
    console.error('Error decoding token:', error)
    return true
  }
}

const handleResponse = async (response, options) => {
  const contentType = response.headers.get('content-type')

  if (contentType && contentType.includes('video/')) {
    return response
  }

  if (
    options.download ||
    (contentType && !contentType.includes('application/json'))
  ) {
    const blob = await response.blob()
    return { blob, status: response.status }
  }

  if (options.binary) {
    return response
  }

  try {
    const data = await response.json()
    return { data, status: response.status, error: null }
  } catch (error) {
    console.error('Error parsing JSON:', error)
    return { data: null, status: response.status, error: error.message }
  }
}

const fetchWithAuth = async (endpoint, options = {}) => {
  const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/${endpoint}`

  const shouldCache =
    (!options.method || options.method === 'GET') && !options.binary

  const cacheKey = shouldCache
    ? JSON.stringify({ url: apiUrl, headers: options.headers })
    : null

  if (shouldCache && cacheKey) {
    const cached = requestCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TIME) {
      return cached.promise
    }
  }

  let accessToken = localStorage.getItem('accessToken')

  if (isTokenExpired(accessToken)) {
    try {
      accessToken = await refreshAccessToken()
    } catch (error) {
      handleAuthFailure()
      return
    }
  }

  const fetchOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  }

  const makeRequest = async () => {
    try {
      const response = await fetch(apiUrl, fetchOptions)

      if (response.status === 401) {
        try {
          accessToken = await refreshAccessToken()
          fetchOptions.headers.Authorization = `Bearer ${accessToken}`
          const retryResponse = await fetch(apiUrl, fetchOptions)
          return handleResponse(retryResponse, options)
        } catch (error) {
          handleAuthFailure()
          return
        }
      }

      return handleResponse(response, options)
    } catch (error) {
      console.error('Error during fetch:', error)
      return { error, status: 500 }
    }
  }

  if (shouldCache && cacheKey) {
    const promise = makeRequest()

    requestCache.set(cacheKey, {
      promise,
      timestamp: Date.now(),
    })

    promise.finally(() => {
      setTimeout(() => requestCache.delete(cacheKey), CACHE_TIME)
    })

    return promise
  }

  return makeRequest()
}

export default fetchWithAuth
